
export type Industry = 
  | 'Technology'
  | 'Finance'
  | 'Healthcare'
  | 'Education'
  | 'Entertainment'
  | 'Retail'
  | 'Manufacturing'
  | 'Energy'
  | 'Hospitality'
  | 'Food & Beverages'
  | 'Web 3.0'
  | 'Other';

export const industries: Industry[] = [
  'Technology',
  'Finance',
  'Healthcare',
  'Education',
  'Entertainment',
  'Retail',
  'Manufacturing',
  'Energy',
  'Hospitality',
  'Food & Beverages',
  'Web 3.0',
  'Other'
];

// Make sure QuestionType is exported as a type
export type QuestionType = 'text' | 'select' | 'radio' | 'scale' | 'likert' | 'binary' | 'dropdown' | 'rating';

// Also create an enum for components that need to reference QuestionType as a value
export enum QuestionTypeEnum {
  TEXT = 'text',
  SELECT = 'select',
  RADIO = 'radio',
  SCALE = 'scale',
  LIKERT = 'likert',
  BINARY = 'binary',
  DROPDOWN = 'dropdown',
  RATING = 'rating'
}

export interface Question {
  id: number;
  text: string;
  type: QuestionType;
  category: string;
  options?: string[];
  required?: boolean;
  minWords?: number;
  scale?: number;
  tooltip?: string;
  explanation?: string;
  example?: string;
  inverted?: boolean;
  weight?: number; // Added question weight for weighted scoring
  conditional?: {
    dependsOn: number;
    showIfValue: any;
  };
  sectionHeader?: string; // Added to support section headers
  benchmark?: {
    industry?: {
      [key: string]: number;
    };
  };
}

export type Answer = string | number | boolean | string[];

export type SurveyResponse = {
  [key: number]: Answer;
};

export interface CategoryScore {
  score: number;
  total: number;
  percentage: number;
  feedback: string;
  weightedScore?: number; // Added weighted score
}

export interface ScoreBreakdown {
  ea: CategoryScore;
  elv: CategoryScore;
  l: CategoryScore;
  tb: CategoryScore;
  bc: CategoryScore;
  
  ee: CategoryScore;
  lq: CategoryScore;
  fc: CategoryScore;
  
  totalNEV: number;
  overallScore: number;
  nev: number;
  eroi: number;
  rating: 'High Risk' | 'Competitive' | 'Exceptional';
  certification?: 'Gold' | 'Silver' | 'Bronze' | null; // Added certification
  
  recommendations: string[];
  keyInsight?: string;
  
  sentimentAnalysis?: {
    dominantEmotions: string[];
    sentimentScore: number;
    detectedIssues: string[];
  };
  pprAlert?: string | null;
  
  submetrics?: {
    emotionalHeatmap?: {
      joy: number;
      trust: number;
      frustration: number;
      indifference: number;
      other: number;
    };
    textSentiments?: any;
    ea?: { 
      fandom: number;
      trust: number;
      referrals: number;
    };
    elv?: { 
      redFlags: string[];
      brightSpots: string[];
    };
    l?: { 
      projection: number[];
    };
  };
  
  riskRadar?: {
    community: number;
    ethics: number;
    funding: number;
    innovation: number;
  };
  
  industryBenchmarks?: {
    [industry: string]: number;
  };
  
  historicalData?: {
    date: string;
    score: number;
  }[];
  
  categories: {
    [category: string]: number;
  };
}

// New types for historical data tracking
export interface HistoricalScore {
  date: string;
  score: number;
  breakdown?: {
    [category: string]: number;
  };
}

export interface IndustryBenchmark {
  industry: string;
  score: number;
  percentile?: number;
}

export interface BEAMCertification {
  level: 'Gold' | 'Silver' | 'Bronze' | null;
  score: number;
  issuedDate?: string;
  validUntil?: string;
}
